import { Helmet } from '../components/Helmet';
import { CheckCircle, Award, Users, BookOpen, BrainCircuit, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AboutPage = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <>
      <Helmet>
        <title>À propos d'Upnov — Expert en formation IA pour entreprises</title>
        <meta
          name="description"
          content="Upnov forme les entreprises à l'IA depuis 2019. Cyril Cohen Solal, expert Data & IA avec 15 ans d'expérience, accompagne PME et grands groupes dans leur transformation IA."
        />
        <meta name="keywords" content="formation IA entreprise, expert IA, Cyril Cohen Solal, Upnov, intelligence artificielle formation" />
        <meta property="og:title" content="À propos d'Upnov — Expert en formation IA pour entreprises" />
        <meta property="og:description" content="Upnov forme les entreprises à l'IA depuis 2019. Cyril Cohen Solal, expert Data & IA avec 15 ans d'expérience, accompagne PME et grands groupes dans leur transformation IA." />
        <meta property="og:url" content="https://www.upnov.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/homepage_share.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-lg mb-6">Notre expertise en formation IA</h1>
            <p className="text-xl mb-8">
              Nous formons les entreprises à l'Intelligence Artificielle depuis plus de 5 ans,
              avec une approche concrète et adaptée aux enjeux professionnels.
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-md mb-6">Notre fondateur</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
              Entrepreneur et expert Data depuis plus de 15 ans, notre fondateur vous accompagne pour une intégration réussie de l’intelligence artificielle au sein de votre entreprise.
            </p>
            <div className="max-w-4xl mx-auto text-left">
              <div className="flex items-center mb-4">
                <img
                  src="/ccs_avatar.webp"
                  alt="Cyril Cohen Solal"
                  width="56"
                  height="56"
                  className="w-14 h-14 rounded-full object-cover mr-4"
                />
                <div>
                  <h3 className="text-xl font-semibold mb-1">Cyril Cohen Solal</h3>
                  <p className="text-gray-500">Conférencier, Expert Data & IA</p>
                </div>
              </div>

              <p className="text-gray-600 mb-4">
                Fort de plus de 15 ans d’expérience en Data et Intelligence Artificielle, Cyril intervient comme consultant et conférencier sur les sujets liés à l’innovation, à la data et à l’IA.
                <br /><br />
                Il anime régulièrement des formations et conférences pour aider les entreprises à mieux comprendre les enjeux de la data et à intégrer concrètement des solutions d’IA et de Big Data dans leurs activités.
                <br /><br />
                Cyril accompagne aussi bien les startups que les grands groupes dans la définition et la mise en œuvre de leur stratégie Data & IA, en alliant vision métier et expertise technologique à travers des missions d’audit, de plan de transformation et de déploiement.
                <br /><br />
                Membre fondateur de la French Tech et expert HighTech reconnu, il intervient régulièrement dans les médias (presse, TV) et a enseigné au Master Data de l’Université Paris Dauphine.
              </p>

              <div className="flex items-center text-primary mb-10">
                <Trophy className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">Spécialiste en Big Data & IA</span>
              </div>
            </div>

          </div>

          {/* 3 photos en colonnes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <img src="/ccs_info1.webp" alt="Cyril Cohen Solal formation IA en entreprise" width="800" height="256" className="rounded-xl w-full h-64 object-cover" />
            <img src="/ccs_info2.webp" alt="Atelier IA collectif Upnov" width="800" height="256" className="rounded-xl w-full h-64 object-cover" />
            <img src="/ccs_info3.webp" alt="Séminaire intelligence artificielle" width="800" height="256" className="rounded-xl w-full h-64 object-cover" />
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <h2 className="heading-md mb-6">Notre histoire</h2>
              <p className="text-gray-600 mb-4">
                Upnov IA est née d'un constat simple : malgré l'importance croissante de l'IA
                dans le monde professionnel, les entreprises manquent souvent des compétences internes
                pour l'adopter efficacement.
              </p>
              <p className="text-gray-600 mb-4">
                Fondée en 2019 par une équipe d'experts en IA et en pédagogie, notre mission est de
                rendre l'Intelligence Artificielle accessible, compréhensible et actionnable pour
                les entreprises de toutes tailles.
              </p>
              <p className="text-gray-600 mb-8">
                Depuis, nous avons formé plus de 1000 professionnels et accompagné plus de 200
                entreprises dans leur transformation numérique grâce à l'IA.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 text-center">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-primary">5+</p>
                  <p className="text-sm text-gray-600">années d'expérience</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-primary">1000+</p>
                  <p className="text-sm text-gray-600">professionnels formés</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-primary">200+</p>
                  <p className="text-sm text-gray-600">entreprises accompagnées</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="relative"
            >
              <img
                src="/equipe.webp"
                alt="L'équipe Upnov IA — experts formation intelligence artificielle"
                width="800"
                height="533"
                className="rounded-xl shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl">
                <p className="text-lg font-bold text-primary">Notre mission</p>
                <p className="text-sm text-gray-600">Démocratiser l'IA pour les entreprises</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-md mb-6">Nos valeurs</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nos formations sont guidées par des valeurs fortes qui garantissent
              leur qualité et leur pertinence pour les entreprises.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="bg-white p-8 rounded-xl shadow-sm"
            >
              <div className="bg-primary/10 p-4 rounded-full w-fit mb-6">
                <BrainCircuit className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Excellence technique</h3>
              <p className="text-gray-600">
                Nous maintenons un niveau d'expertise technique élevé avec des formateurs
                expérimentés et un contenu constamment mis à jour.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="bg-white p-8 rounded-xl shadow-sm"
            >
              <div className="bg-primary/10 p-4 rounded-full w-fit mb-6">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Approche pratique</h3>
              <p className="text-gray-600">
                Nous privilégions les cas concrets et les exercices pratiques pour assurer
                une application immédiate des compétences acquises.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="bg-white p-8 rounded-xl shadow-sm"
            >
              <div className="bg-primary/10 p-4 rounded-full w-fit mb-6">
                <BookOpen className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Pédagogie innovante</h3>
              <p className="text-gray-600">
                Nous concevons des expériences d'apprentissage engageantes adaptées
                à différents profils et niveaux d'expertise.
              </p>
            </motion.div>
          </div>
        </div>
      </section>




      {/* Certifications Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <h2 className="heading-md mb-6">Certifications et accréditations</h2>
              <p className="text-gray-600 mb-6">
                Nos formations sont reconnues pour leur qualité et leur conformité aux
                standards professionnels en matière de formation.
              </p>
              <ul className="space-y-4">
                {/*                 <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-primary shrink-0 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium">Organisme de formation certifié Qualiopi</p>
                    <p className="text-sm text-gray-500">Certification qualité délivrée au titre des actions de formation</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-primary shrink-0 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium">Certifications reconnues par France Compétences</p>
                    <p className="text-sm text-gray-500">Formations éligibles au financement par les OPCO</p>
                  </div>
                </li> */}
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-primary shrink-0 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium">Partenaires académiques</p>
                    <p className="text-sm text-gray-500">Collaborations avec des universités et écoles d'ingénieurs</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-primary shrink-0 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium">Partenaires technologiques</p>
                    <p className="text-sm text-gray-500">Certifiés par les leaders de l'industrie IA</p>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-white p-6 rounded-lg shadow-sm flex items-center justify-center">
                <Award className="h-16 w-16 text-primary" />
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm flex items-center justify-center">
                <Award className="h-16 w-16 text-primary" />
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm flex items-center justify-center">
                <Award className="h-16 w-16 text-primary" />
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm flex items-center justify-center">
                <Award className="h-16 w-16 text-primary" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-16">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="heading-md mb-6">Prêt à former vos équipes ?</h2>
            <p className="text-xl mb-8">
              Contactez-nous pour discuter de vos besoins en formation IA et découvrir comment
              nous pouvons vous aider à développer les compétences de vos équipes.
            </p>
            <Link to="/DemandeInfo/" className="btn-accent">
              Demander un devis gratuit
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;